import {
  IvaLabel,
  PriceDisplayType,
  priceFormat,
  Translation,
  usePlatformConfig,
  usePriceFormatter,
  useWidgetContext,
  WidgetTypes,
} from '@dxp-module/common'
import { Box } from '@mui/material'
import { FC, MutableRefObject } from 'react'
import { useIntl } from 'react-intl'
import BreakdownElementKey from '../../../enum/BreakdownElementKey'
import {
  BreakdownBlockRow,
  Tooltip,
} from '../../../../common/graphql/__generated__/graphql.autogenerated'
import {
  BreakdownTooltip,
  FinanceBreakdownElementBlock,
} from '../../../../common/model/Breakdown'
import TranslationElements from '../../../enum/TranslationElements'
import AccordionElement from '../elements/AccordionElement/AccordionElement'
import TooltipElement from '../elements/TooltipElement/TooltipElement'
import financeBreakdownElementStyles from './financeBreakdownElement.styles'

type FinanceBreakdownElementProps = {
  element: BreakdownBlockRow | FinanceBreakdownElementBlock
  index: number
  carGroup?: string
  targetRef?: MutableRefObject<undefined>
  isSecondaryCurrencyDisplay: boolean
}

const FinanceBreakdownElement: FC<FinanceBreakdownElementProps> = ({
  element,
  index,
  targetRef,
  carGroup,
  isSecondaryCurrencyDisplay,
}) => {
  const styles = financeBreakdownElementStyles()
  const { title, value, isNegative, key, shouldBeFormatted } = element

  const {
    platformConfig: {
      priceDisplay,
      financeCalculator: { showNegativeSignReservationFee, showFinanceTooltip },
    },
  } = usePlatformConfig()

  const {
    configuration: {
      dimensions: { language },
    },
  } = useWidgetContext()

  const isBreakdownBlockRow = (
    element: BreakdownBlockRow | FinanceBreakdownElementBlock
  ): element is BreakdownBlockRow => {
    return '__typename' in element && element.__typename === 'BreakdownBlockRow'
  }

  const intl = useIntl()

  // Not using the Translation component is intentional. Not all titles are translation keys, but intl will return
  // the same string if it doesn't have a translation its messages object, which is convenient for us, because
  // we can display both translations, and normal text, like Discount labels.
  const translatedTitle = title ? intl.formatMessage({ id: title }) : ''

  const {
    currency,
    currencyFormatConfig,
    decimalSeparator,
    thousandSeparator,
    decimalScale,
  } =
    priceDisplay[
      isSecondaryCurrencyDisplay
        ? PriceDisplayType.SECONDARY
        : PriceDisplayType.PRIMARY
    ] || {}

  const pricePrimaryFormatter = usePriceFormatter({
    currency,
    currencyFormatConfig,
    decimals: priceDisplay?.finance?.decimalScale ?? decimalScale,
    separator: priceDisplay?.finance?.thousandSeparator ?? thousandSeparator,
    decimalSeparator:
      priceDisplay?.finance?.decimalSeparator ?? decimalSeparator,
  })

  let formattedValue: string | number = value
  let suffix: JSX.Element | '' = ''
  switch (key) {
    case BreakdownElementKey.MILEAGE:
      if (value) {
        formattedValue = (+value)?.toLocaleString(language)
        suffix = (
          <Translation id="trans__fincalc__finance_breakdown__mileage_unit" />
        )
      }
      break
    case BreakdownElementKey.REPAYMENT:
    case BreakdownElementKey.VEHICLE_NAMEPRICE:
      suffix = (
        <IvaLabel
          group={carGroup}
          component="span"
          dataTest="text:ivaLabel"
          className={styles.textSuffix}
          widget={WidgetTypes.FINANCE_CALCULATOR}
        />
      )
      break
    case BreakdownElementKey.CO_TWO:
      suffix = (
        <Translation id="trans__fincalc__cash_breakdown__co2emissions_unit" />
      )
      break
    default:
      break
  }

  const isSetToNegative =
    title === TranslationElements.RESERVATION_FEE &&
    showNegativeSignReservationFee
      ? true
      : isNegative
  formattedValue = shouldBeFormatted
    ? priceFormat.formatMinorPrice(
        +value,
        pricePrimaryFormatter,
        isSetToNegative
      )
    : formattedValue

  const createTooltipElement = (
    highlighted: boolean,
    tooltip?: Tooltip | BreakdownTooltip[] | null
  ) => {
    return (
      <TooltipElement
        index={index}
        title={title}
        translatedTitle={translatedTitle}
        translationKey={title}
        tooltip={tooltip}
        price={formattedValue}
        suffix={suffix}
        shouldBeFormatted={shouldBeFormatted}
        value={value}
        highlighted={highlighted}
        targetRef={targetRef}
      />
    )
  }
  const renderTooltipElement = () => {
    if (isBreakdownBlockRow(element)) {
      const { toolTip, highlighted } = element
      return createTooltipElement(highlighted, toolTip)
    }
    const { tooltips, style } = element
    return createTooltipElement(style?.fontWeight === 'bold', tooltips)
  }

  return (
    <Box
      key={title}
      className={styles.breakdownElementContainer}
      data-test={`summary:finance_breakdown_element_${index}`}
    >
      {showFinanceTooltip ? (
        <AccordionElement
          index={index}
          value={value}
          title={title}
          translatedTitle={translatedTitle}
          suffix={suffix}
          price={formattedValue}
        />
      ) : (
        renderTooltipElement()
      )}
    </Box>
  )
}

export default FinanceBreakdownElement
